import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import classnames from 'classnames/bind'
import { useTranslation } from 'next-i18next'
import { toast } from 'react-toastify'

// Components
import Icon from '@/assets/icons'
import CategoryPreferencePicker from '.'

// Libs
import { useAppSelector } from '@/redux/hooks'
import { isClientSideShopApp } from '@/utils/navigator'
import { useCategoryPreference } from './useCategoryPreference'

// Constants
import { ROLE_TYPE } from '@/constants/roleType'
import { STORAGE_DATA } from '@/constants/storageData'

// Styles
import styles from './style.module.scss'

// Variables
const cx = classnames.bind(styles)

// 選到這麼多個以上，推薦就夠準；只是提示，不強制
const SUGGESTED_COUNT = 3

// 首頁用：登入會員還沒回答過偏好分類時跳出來問。
// 「略過」存空陣列，之後不再問；按右上角關閉只在這次瀏覽（sessionStorage）不再跳，下次開站再問。
// 會跳升級 / 特別禮 Dialog 的時候不跳，避免疊兩個 Dialog；特別禮按過「不再提醒」就輪到這個。
const CategoryPreferenceDialog = () => {
  const { t } = useTranslation()
  const memberData = useAppSelector((state) => state.member?.memberData)
  const { isLogin, preference, save, isSaving } = useCategoryPreference()
  const [isOpen, setIsOpen] = useState(false)
  const [value, setValue] = useState<number[]>([])

  useEffect(() => {
    if (!isLogin || !preference || preference.answered || isClientSideShopApp) return
    if (!preference.options.length || memberData?.needsUpgrade) return
    try {
      if (window.sessionStorage.getItem(STORAGE_DATA.CATEGORY_PREFERENCE_DISMISSED)) return
      // 與 ProfileGiftDialog 同樣的判斷：資料未完整的一般會員、且沒按過特別禮「不再提醒」時，特別禮 Dialog 會跳
      const isMerchant = Array.isArray(memberData?.roles) && memberData.roles.includes(ROLE_TYPE.MERCHANT)
      const giftDismissed = window.localStorage.getItem(`${STORAGE_DATA.PROFILE_GIFT_REMINDER_DISMISSED}:${memberData?.memberInfoId ?? ''}`)
      if (memberData?.isProfileCompleted === false && !isMerchant && !giftDismissed) return
    } catch {
      // storage 不能用就照常跳
    }
    setIsOpen(true)
  }, [isLogin, preference, memberData])

  // 開著的時候鎖住背景捲動、可以按 Esc 關閉
  useEffect(() => {
    if (!isOpen) return
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismissForSession()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = overflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const dismissForSession = () => {
    try {
      window.sessionStorage.setItem(STORAGE_DATA.CATEGORY_PREFERENCE_DISMISSED, 'true')
    } catch {
      // 不能記就算了，只是下次換頁會再跳
    }
    setIsOpen(false)
  }

  const handleSave = async (categoryIds: number[]) => {
    try {
      await save(categoryIds)
      setIsOpen(false)
      if (categoryIds.length) toast.success(t('Category_Preference_Saved') as string)
    } catch {
      toast.error(t('Category_Preference_Save_Failed') as string)
    }
  }

  if (!isOpen || typeof document === 'undefined') return null

  const progress = !value.length ? (
    t('Category_Preference_Hint', { count: SUGGESTED_COUNT })
  ) : (
    <>
      <strong>{t('Category_Preference_Selected', { count: value.length })}</strong>
      {' · '}
      {value.length < SUGGESTED_COUNT ? t('Category_Preference_More', { count: SUGGESTED_COUNT - value.length }) : t('Category_Preference_Enough')}
    </>
  )

  return createPortal(
    <div className={cx('category-preference-dialog')} role='dialog' aria-modal='true' aria-labelledby='category-preference-title'>
      <div className={cx('category-preference-dialog__backdrop')} onClick={dismissForSession} />
      <div className={cx('category-preference-dialog__panel')}>
        <div className={cx('category-preference-dialog__header')}>
          <span className={cx('category-preference-dialog__handle')} />
          <span className={cx('category-preference-dialog__eyebrow')}>{t('Home_For_You_Title')}</span>
          <h2 id='category-preference-title' className={cx('category-preference-dialog__title')}>
            {t('Category_Preference_Dialog_Title')}
          </h2>
          <p className={cx('category-preference-dialog__description')}>{t('Category_Preference_Dialog_Description')}</p>
          <button
            type='button'
            className={cx('category-preference-dialog__close')}
            aria-label={t('Common_Close') as string}
            onClick={dismissForSession}
          >
            <Icon.Close />
          </button>
        </div>
        <div className={cx('category-preference-dialog__body')}>
          <CategoryPreferencePicker options={preference?.options ?? []} value={value} onChange={setValue} />
        </div>
        <div className={cx('category-preference-dialog__footer')}>
          <span className={cx('category-preference-dialog__progress')}>{progress}</span>
          <button type='button' className={cx('category-preference-dialog__skip')} disabled={isSaving} onClick={() => handleSave([])}>
            {t('Category_Preference_Skip')}
          </button>
          <button
            type='button'
            className={cx('category-preference-dialog__submit')}
            disabled={isSaving || !value.length}
            onClick={() => handleSave(value)}
          >
            {t('Category_Preference_Submit')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default CategoryPreferenceDialog
